import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Table,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Dropdown,
  InputGroup,
} from "react-bootstrap";

import {
  fetchAdminUsersThunk,
  fetchUserPermissionsThunk,
  saveUserPermissionsThunk,
  setSelectedUserId,
  updateCell,
  toggleRowAll,
  toggleColumnAll,
} from "../../../store/slices/adminPermissionSlice";
import { toast } from "react-toastify";

export default function UserPermissionManager() {
  const dispatch = useDispatch();

  const [userSearch, setUserSearch] = useState("");
  const [moduleSearch, setModuleSearch] = useState(""); // ✅ MODULE SEARCH

  const {
    users,
    selectedUserId,
    rows,
    loadingUsers,
    loadingPerms,
    saving,
    error,
  } = useSelector((s) => s.adminPerm);

  useEffect(() => {
    dispatch(fetchAdminUsersThunk());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUserId) dispatch(fetchUserPermissionsThunk(selectedUserId));
  }, [dispatch, selectedUserId]);

  const selectedUser = useMemo(
    () => (users || []).find((u) => String(u.uid) === String(selectedUserId)),
    [users, selectedUserId]
  );

  // ✅ USER SEARCH (dropdown)
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter((u) =>
      `${u.username} ${u.role}`.toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  // ✅ MODULE SEARCH (table filter)
  const filteredRows = useMemo(() => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => {
      const hay = `${r.module_name || ""} ${r.module_key || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, moduleSearch]);

  const canUseBulk = !!selectedUserId && !!(rows || []).length;

  const onSave = async () => {
    if (!selectedUserId) {
      toast.warn("Please select a user");
      return;
    }

    const res = await dispatch(
      saveUserPermissionsThunk({ user_id: selectedUserId, rows })
    );

    if (res?.meta?.requestStatus === "fulfilled") {
      await dispatch(fetchUserPermissionsThunk(selectedUserId));
      toast.success("Permissions updated ✅");
    } else {
      const msg =
        res?.payload?.message ||
        res?.error?.message ||
        "Failed to update permissions";
      toast.error(msg);
    }
  };

  const bulkButtons = [
    { label: "View: ON", field: "can_view", value: true },
    { label: "View: OFF", field: "can_view", value: false },
    { label: "Create: ON", field: "can_create", value: true },
    { label: "Create: OFF", field: "can_create", value: false },
    { label: "Update: ON", field: "can_update", value: true },
    { label: "Update: OFF", field: "can_update", value: false },
    { label: "Delete: ON", field: "can_delete", value: true },
    { label: "Delete: OFF", field: "can_delete", value: false },
  ];

  return (
    <div className="container-fluid upm-wrap">
      <Row>
        <Col xl={12}>
          <Card className="upm-card">
            <Card.Header className="upm-header">
              <div>
                <div className="upm-title">User Permission Manager</div>
                <div className="upm-subtitle">
                  Choose a user and toggle module permissions
                </div>
              </div>

              <div className="upm-actions">
                <Button
                  className="upm-btn upm-btn-light"
                  onClick={() => dispatch(fetchAdminUsersThunk())}
                  disabled={loadingUsers}
                >
                  {loadingUsers ? "Refreshing..." : "Refresh Users"}
                </Button>

                <Button
                  className="upm-btn upm-btn-primary"
                  onClick={onSave}
                  disabled={!selectedUserId || saving || loadingPerms}
                >
                  {saving ? "Saving..." : "Save Permissions"}
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="upm-body">
              {error ? <Alert variant="danger">{error}</Alert> : null}

              <Row className="g-3 align-items-end">
                <Col md={6}>
                  <Form.Group className="upm-field">
                    <Form.Label className="upm-label">Select User</Form.Label>

                    <Dropdown className="custom-dropdown upm-user-dd">
                      <Dropdown.Toggle
                        variant="primary"
                        className="upm-user-toggle"
                        disabled={loadingUsers}
                      >
                        <i className="fa fa-user" />
                        {selectedUser
                          ? `${selectedUser.username} (${selectedUser.role})`
                          : "Choose user"}
                        <i className="fa fa-angle-down ms-auto" />
                      </Dropdown.Toggle>

                      {/* ✅ prevent close when clicking inside */}
                      <Dropdown.Menu
                        className="upm-user-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-3 pt-2 pb-2">
                          <InputGroup size="sm">
                            <InputGroup.Text>
                              <i className="fa fa-search" />
                            </InputGroup.Text>
                            <Form.Control
                              placeholder="Search user..."
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()} // ✅ keep open while typing
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                            {userSearch ? (
                              <Button
                                variant="light"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setUserSearch("");
                                }}
                                title="Clear"
                              >
                                ×
                              </Button>
                            ) : null}
                          </InputGroup>
                        </div>

                        <div className="upm-user-list">
                          <Dropdown.Item
                            active={!selectedUserId}
                            onClick={() => dispatch(setSelectedUserId(""))}
                          >
                            -- Select --
                          </Dropdown.Item>

                          <div className="dropdown-divider" />

                          {(filteredUsers || []).map((u) => (
                            <Dropdown.Item
                              key={u.uid}
                              active={String(u.uid) === String(selectedUserId)}
                              onClick={() => dispatch(setSelectedUserId(u.uid))}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">{u.username}</span>
                                <span className="upm-role">{u.role}</span>
                              </div>
                            </Dropdown.Item>
                          ))}

                          {!filteredUsers?.length ? (
                            <div className="text-muted px-3 py-2 small">
                              No users found
                            </div>
                          ) : null}
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>

                    {loadingUsers ? (
                      <div className="mt-2 text-muted small">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading users...
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  {selectedUser ? (
                    <div className="upm-editing">
                      <span className="text-muted">Editing:</span>{" "}
                      <b>{selectedUser.username}</b>
                      <span className="upm-pill">{selectedUser.role}</span>
                    </div>
                  ) : (
                    <div className="upm-editing text-muted">
                      Pick a user to begin
                    </div>
                  )}
                </Col>
              </Row>

              {/* ✅ TOOLBAR: bulk buttons + MODULE SEARCH */}
              <div className="upm-toolbar d-flex flex-wrap gap-3 justify-content-between align-items-center">
                <div className="upm-chip-group">
                  {bulkButtons.map((b) => (
                    <Button
                      key={b.label}
                      size="sm"
                      className="upm-chip"
                      onClick={() =>
                        dispatch(toggleColumnAll({ field: b.field, value: b.value }))
                      }
                      disabled={!canUseBulk}
                    >
                      {b.label}
                    </Button>
                  ))}
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <InputGroup size="sm" style={{ maxWidth: 360 }}>
                    <InputGroup.Text>
                      <i className="fa fa-search" />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search module (name / key)..."
                      value={moduleSearch}
                      onChange={(e) => setModuleSearch(e.target.value)}
                    />
                    {moduleSearch ? (
                      <Button
                        variant="light"
                        onClick={() => setModuleSearch("")}
                        title="Clear"
                      >
                        ×
                      </Button>
                    ) : null}
                  </InputGroup>

                  <div className="small text-muted">
                    Showing <b>{filteredRows.length}</b> / {(rows || []).length}
                  </div>

                  {loadingPerms ? (
                    <div className="upm-loading ms-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="upm-table-wrap">
                <Table className="upm-table" hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>All</th>
                      <th>Module</th>
                      <th>Key</th>
                      <th style={{ width: 120 }}>View</th>
                      <th style={{ width: 120 }}>Create</th>
                      <th style={{ width: 120 }}>Update</th>
                      <th style={{ width: 120 }}>Delete</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(filteredRows || []).map((r) => (
                      <tr key={r.module_key}>
                        <td className="text-center">
                          <Form.Check
                            className="upm-check"
                            type="checkbox"
                            checked={
                              r.can_view && r.can_create && r.can_update && r.can_delete
                            }
                            onChange={(e) =>
                              dispatch(
                                toggleRowAll({
                                  moduleKey: r.module_key,
                                  value: e.target.checked,
                                })
                              )
                            }
                          />
                        </td>

                        <td className="upm-module">{r.module_name}</td>
                        <td className="upm-key">{r.module_key}</td>

                        {["can_view", "can_create", "can_update", "can_delete"].map(
                          (field) => (
                            <td className="text-center" key={field}>
                              <Form.Check
                                className="upm-check"
                                type="checkbox"
                                checked={!!r[field]}
                                onChange={(e) =>
                                  dispatch(
                                    updateCell({
                                      moduleKey: r.module_key,
                                      field,
                                      value: e.target.checked,
                                    })
                                  )
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                    ))}

                    {!filteredRows.length ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          {moduleSearch
                            ? "No modules match your search."
                            : "Select a user to view modules & permissions."}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}